import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { APIType } from '~/backend/types';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';
import { useReferenceDose } from '~/hooks/dose/useReferenceDose';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { resolveCarriedDose } from '~/hooks/workflows/backend/tasks/carriedDose';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { usePlanAndTargetScans } from './usePlanAndTargetScans';
import { useRegistration } from '~/hooks/workflows/backend/tasks/useRegistration';
import { useBackendModels } from '~/hooks/workflows/backend/useBackend';
import { contourPropagationTaskState } from '~/state/atoms/adapt/contourPropagationTaskAtom';
import {
    replanningPropagationSubmittingState,
    replanningRunSummaryState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import { isJobRunning, jobQueueState } from '~/state/atoms/jobAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { WorkflowTypes } from '~/state/atoms/viewerAtoms';
import {
    structuresToContour,
    structuresToTransform,
} from './roiPropagationPlan';
import { useRoiPlan } from './useRoiPlan';

export interface PropagationRun {
    readonly run: () => void;
    readonly canRun: boolean;
    readonly isRunning: boolean;
    readonly blockedReasonId?: string;
}

export function usePropagationRunning(): boolean {
    const submitting = useRecoilValue(replanningPropagationSubmittingState);
    const { jobs } = useRecoilValue(jobQueueState);

    return (
        submitting ||
        jobs.some(
            (job) =>
                job.type === WorkflowTypes.ADAPT && isJobRunning(job.status)
        )
    );
}

export function usePropagationRun(): PropagationRun {
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const task = useRecoilValue(contourPropagationTaskState);
    const { data: registrationModels } = useBackendModels(APIType.REGISTRATION);
    const { startProcessing } = useRegistration();
    const setRunSummary = useSetRecoilState(replanningRunSummaryState);
    const [submitting, setSubmitting] = useRecoilState(
        replanningPropagationSubmittingState
    );
    const isRunning = usePropagationRunning();
    const { entries } = useRoiPlan();
    const patient = useSelectedPatient();
    const currentDose = useCurrentDose();
    const referenceDose = useReferenceDose();

    const chosenModel = (registrationModels ?? []).find(
        (model) => model.name === task.propagationModelName
    );
    const scans = usePlanAndTargetScans();
    const primaryImage = useSelectedPrimaryImage();
    const targetSeriesUid = scans.target?.seriesUid;
    // The run registers the displayed scan; it must be the source Plan & Target names.
    const onPlanSource =
        !!scans.source && primaryImage?.seriesUid === scans.source.seriesUid;

    const carried = resolveCarriedDose(
        patient?.doses ?? [],
        currentDose?.sopInstanceUid,
        referenceDose,
        scans.source?.frameOfReferenceUid
    );
    const carriedDose = carried.status === 'carried' ? carried.dose : undefined;

    const owesInitRigid =
        task.initRigid?.enabled === true && !task.initRigid.sopInstanceUid;
    const canRun =
        !isRunning &&
        !!chosenModel &&
        !!targetSeriesUid &&
        onPlanSource &&
        !!currentStructureSet &&
        !owesInitRigid &&
        carried.status !== 'otherFrame';
    const blockedReasonId = !targetSeriesUid
        ? 'replanning.run.noTarget'
        : !onPlanSource
          ? 'replanning.run.sourceNotShown'
          : owesInitRigid
            ? 'replanning.initRigid.required'
            : carried.status === 'otherFrame'
              ? 'replanning.run.doseOtherFrame'
              : undefined;

    const run = () => {
        if (!canRun || !chosenModel || !targetSeriesUid || submitting) {
            return;
        }
        setSubmitting(true);
        const propagated = structuresToTransform(entries);
        const contoured = structuresToContour(entries);
        setRunSummary({
            modelName: chosenModel.name,
            propagatedCount: propagated.length - contoured.length,
            segmentedCount: contoured.length,
            initRigidSopInstanceUid: task.initRigid?.enabled
                ? task.initRigid.sopInstanceUid
                : undefined,
            priorDoseSopInstanceUid: carriedDose?.sopInstanceUid,
        });
        void startProcessing({
            selectedModel: chosenModel,
            targetImageSeriesUid: targetSeriesUid,
            selectedStructures: propagated,
            initRigidSopInstanceUid: task.initRigid?.enabled
                ? task.initRigid.sopInstanceUid
                : undefined,
            doseSopInstanceUid: carriedDose?.sopInstanceUid,
            contourRois: contoured,
            contourModel: task.comparisonModel,
        }).finally(() => setSubmitting(false));
    };

    return { run, canRun, isRunning, blockedReasonId };
}
